import { useEffect, useMemo } from 'react';
import { filterArticleTargets } from './articleNumbers';
import { useArticleAvailability } from './useArticleAvailability';
import { useAvailableArticleRange } from './useAvailableArticleRange';

interface ClashOptions<T> {
  targets: T[];
  availability: ReturnType<typeof useArticleAvailability>;
  mode: 'single' | 'range';
  itemCode: string;
  start: number;
  end: number;
  padding: number;
  position: 'before' | 'after';
  minimumLength: number;
  maximumLength: number;
  ignoreClashes: boolean;
  onIgnoreClashesChange: (value: boolean) => void;
  allowErpImport?: boolean;
  isErpImportSelected?: (itemCode: string) => boolean;
}

export function useArticleClashes<T extends { itemCode: string }>({
  targets,
  availability,
  mode,
  itemCode,
  start,
  end,
  padding,
  position,
  minimumLength,
  maximumLength,
  ignoreClashes,
  onIgnoreClashesChange,
  allowErpImport = false,
  isErpImportSelected,
}: ClashOptions<T>) {
  const occupied = targets.map((target) => {
    const error = availability.data?.[target.itemCode]?.errorType;
    return (
      error === 'InUseByTexas' ||
      error === 'ArchivedInTexas' ||
      (!allowErpImport && error === 'InUseByErp')
    );
  });
  const firstArticleClashes = occupied[0] === true;
  const laterArticleClashes = occupied.slice(1).some(Boolean);
  const suggestedRange = useAvailableArticleRange({
    enabled:
      firstArticleClashes &&
      mode === 'range' &&
      !availability.loading &&
      !availability.error,
    itemCode,
    start,
    end,
    padding,
    position,
    minimumLength,
    maximumLength,
    knownAvailability: availability.data,
  });
  useEffect(() => {
    if (
      !firstArticleClashes &&
      laterArticleClashes &&
      !availability.loading &&
      !availability.error
    )
      onIgnoreClashesChange(true);
  }, [
    firstArticleClashes,
    laterArticleClashes,
    availability.loading,
    availability.error,
    onIgnoreClashesChange,
  ]);
  const creationTargets = useMemo(
    () =>
      firstArticleClashes
        ? []
        : filterArticleTargets(targets, ignoreClashes, (code) => {
            const error = availability.data?.[code]?.errorType;
            return (
              error === 'InUseByTexas' ||
              error === 'ArchivedInTexas' ||
              (error === 'InUseByErp' && !isErpImportSelected?.(code))
            );
          }),
    [
      targets,
      firstArticleClashes,
      ignoreClashes,
      availability.data,
      isErpImportSelected,
    ],
  );
  return {
    firstArticleClashes,
    clashingItemCodeCount: occupied.filter(Boolean).length,
    creationTargets,
    ignoredCount: firstArticleClashes
      ? 0
      : targets.length - creationTargets.length,
    suggestedRange,
  };
}
